import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { blendHexWithOverlay, fontDisplay, hexToRgbA, StylesIndexInjections } from 'common-react';
import { InlineButtonSizeModel, InlineButtonStylePropsModel } from './InlineButton.types';
import { themeColorsRgba } from '../theme/tools/theme.utils';
import { focusStyle } from '../styles/mixins';

const getFontSize = (theme: Theme, size: InlineButtonSizeModel): string => {
  if ( size === 'medium') {
    return fontDisplay('normal', theme.fontSize.xl, 36);
  }
  if (size === 'default') {
    return fontDisplay('normal', theme.fontSize.base, 24);
  }
  return fontDisplay('normal', theme.fontSize.s, 20);
};

export default makeUiStyles<Theme, InlineButtonStylePropsModel, 'root' | 'content' | 'icon' | 'spinner' | 'isLoading'>((theme: Theme) => {
  const textColor = theme.color.greys[80];
  const overlayColor = theme.color.greys[80];
  const underlineColor = theme.color.violets[100];
  const hoverOpacity = 0.08;
  const pressedOpacity = 0.16;
  const hoverTextColor = blendHexWithOverlay(textColor, overlayColor, hoverOpacity);
  const pressedTextColor = blendHexWithOverlay(textColor, overlayColor, pressedOpacity);
  const hoverUnderlineColor = blendHexWithOverlay(underlineColor, overlayColor, hoverOpacity);
  const pressedUnderlineColor = blendHexWithOverlay(underlineColor, overlayColor, pressedOpacity);

  return createStyles({
    root: {
      position: 'relative',
      verticalAlign: 'baseline',
      '&:focus-visible': {
        ...focusStyle(theme),
      },
      '&.Mui-disabled': {
        '& $content': {
          opacity: 0.4,
          cursor: 'not-allowed',
        }
      }
    },
    content: {
      font: ({ size }: InlineButtonStylePropsModel) => getFontSize(theme, size),
      color: textColor,
      border: 'none',
      backgroundColor: 'transparent',
      margin: 0,
      padding: 0,
      cursor: 'pointer',
      display: 'inline-flex',
      gap: 4,
      alignItems: 'center',
      textDecorationLine: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? 'underline' : 'none',
      textDecorationColor: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? underlineColor : 'inherit',
      textDecorationStyle: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? 'solid' : undefined,
      textDecorationSkipInk: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? 'none' : undefined,

      '&:hover': {
        color: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? hoverTextColor : theme.color.greys[80],
        backgroundColor: ({ variant }: InlineButtonStylePropsModel) =>
          variant === 'underline' ? hexToRgbA(overlayColor, hoverOpacity) : themeColorsRgba(theme).black8,
        textDecorationLine: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? 'underline' : undefined,
        textDecorationColor: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? hoverUnderlineColor : undefined,
      },

      '&:active, &:hover:active': {
        color: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? pressedTextColor : undefined,
        backgroundColor: ({ variant }: InlineButtonStylePropsModel) =>
          variant === 'underline' ? hexToRgbA(overlayColor, pressedOpacity) : undefined,
        textDecorationColor: ({ variant }: InlineButtonStylePropsModel) => variant === 'underline' ? pressedUnderlineColor : undefined,
      },
    },
    icon: {
      width: 16,
      height: 16,
      color: theme.color.greys[80],
      position: 'relative',
      transition: 'transform 0.2s ease-in-out',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    isLoading: {
      opacity: 0,
      pointerEvents: 'none',
    },
  });
}, { name: 'InlineButton', index: StylesIndexInjections.UI_ATOMS });
